import React, { useEffect, useMemo, useState } from 'react';
import type { FieldManifest, FieldOption, FieldValue } from '../../../../core/fields/types';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Checkbox } from '../../../../components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../../components/ui/command';
import { useTranslation } from '@/hooks/useTranslation';
import { Tracker } from '../../../../domains/analytics/services/Tracker';
import { MEDICAL } from '../../../../domains/analytics/constants/MetricEvents';

interface MultiSelectRendererProps {
  field: FieldManifest;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  readOnlyValues?: string[];
  readOnlyTooltip?: string;
  onOpenChange?: (open: boolean) => void;
  closeOnCustomCreate?: boolean;
}

const arraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const MultiSelectRenderer: React.FC<MultiSelectRendererProps> = ({ field, value, onChange, readOnlyValues, readOnlyTooltip, onOpenChange, closeOnCustomCreate }) => {
  const [customInput, setCustomInput] = useState('');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedValues = useMemo(() => {
    if (!Array.isArray(value)) return [];
    // Handle mixed arrays: strings pass through, objects extract id/description, skip invalid
    return value
      .map((v) => {
        if (typeof v === 'string') return v;
        if (v && typeof v === 'object') return (v as any).id ?? (v as any).description ?? null;
        return String(v);
      })
      .filter((v): v is string => v != null);
  }, [value]);
  const [localSelected, setLocalSelected] = useState<string[]>(selectedValues);
  const readOnlySet = useMemo(() => new Set((readOnlyValues ?? []).map(String)), [readOnlyValues]);

  useEffect(() => {
    setLocalSelected(prev => (arraysEqual(prev, selectedValues) ? prev : selectedValues));
  }, [selectedValues]);

  const options = field.options || [];
  const { t } = useTranslation('genogram');
  const { t: tDiagram } = useTranslation('diagram');
  const translate = (s?: string) => (s && s.startsWith('fields.') ? (t(s, s) as string) : (s ?? ''));
  const selectPlaceholder =
    translate(field.placeholder) ||
    (tDiagram('orgchart.propertyPanel.selectPlaceholder', { defaultValue: 'Select...' }) as string);
  const optionMap = useMemo(() => {
    const map = new Map<string, FieldOption>();
    options.forEach(option => {
      map.set(option.value, option);
    });
    return map;
  }, [options]);

  // Medical field IDs that need tracking
  const medicalFieldIds = ['medicalConditionTags', 'geneticMarkers', 'substanceUse', 'intergenerationalPatterns'];
  const isMedicalField = medicalFieldIds.includes(field.id);

  const handleToggle = (optionValue: string) => {
    if (readOnlySet.has(optionValue)) return;
    if (field.readonly) return;

    const wasSelected = localSelected.includes(optionValue);
    const newValues = (wasSelected
      ? localSelected.filter(v => v !== optionValue)
      : [...localSelected, optionValue]);

    setLocalSelected(newValues);
    onChange(newValues);

    // Track medical field interactions
    if (isMedicalField) {
      Tracker.track(MEDICAL.FIELD_OPTION_TOGGLED, {
        field_id: field.id,
        field_name: field.label,
        option_value: optionValue,
        action: wasSelected ? 'deselected' : 'selected',
        total_selected: newValues.length,
        is_custom_option: !field.options?.some(opt => opt.value === optionValue),
        selected_count: 1, // Default to 1, can be enhanced if bulk update context is available
      });
    }
  };

  const handleAddCustom = (val?: string) => {
    const source = (val ?? customInput).trim();
    if (!source || !field.allowCustomOptions) return;

    if (!localSelected.includes(source)) {
      const next = [...localSelected, source];
      setLocalSelected(next);
      onChange(next);

      // Track custom medical field option creation
      if (isMedicalField) {
        Tracker.track(MEDICAL.FIELD_CUSTOM_OPTION_CREATED, {
          field_id: field.id,
          field_name: field.label,
          custom_value: source,
          total_selected: next.length,
        });
      }
    }
    if (!val) setCustomInput('');
    if (closeOnCustomCreate) {
      setOpen(false);
      onOpenChange?.(false);
    }
  };

  const getOption = (val: string) => optionMap.get(val);

  const renderOptionVisual = (option: FieldOption | undefined, fallback: string) => (
    <span className="inline-flex items-center gap-1">
      {option?.color ? (
        <span
          className="inline-block w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: option.color }}
          aria-hidden="true"
        />
      ) : null}
      {option?.icon ? (
        <span className="text-sm" aria-hidden="true">{option.icon}</span>
      ) : null}
      <span>{option?.label || fallback}</span>
    </span>
  );

  return (
    <div className="space-y-2">
      <Popover
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (v) {
            setLocalSelected(selectedValues);
          }
          onOpenChange?.(v);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className={cn(
              'w-full justify-start h-auto min-h-9 py-1.5 font-normal',
              !localSelected.length && 'text-muted-foreground'
            )}
            disabled={field.readonly}
            data-testid={`tid.genogram.field.${field.id}.multi`}
          >
            <div className="flex items-center gap-1 flex-wrap flex-1">
              {localSelected.length > 0 ? (
                <>
                  {localSelected.slice(0, 2).map(val => {
                    const isReadOnly = readOnlySet.has(val);
                    return (
                      <Badge
                        key={val}
                        variant="secondary"
                        className={cn('font-normal', isReadOnly && 'bg-muted text-muted-foreground border border-dashed')}
                        title={isReadOnly ? readOnlyTooltip : undefined}
                      >
                        {renderOptionVisual(getOption(val), translate(getOption(val)?.label) || val)}
                      </Badge>
                    );
                  })}
                  {localSelected.length > 2 && (
                    <Badge variant="secondary" className="font-normal">
                      +{localSelected.length - 2}
                    </Badge>
                  )}
                </>
              ) : (
                <span className="text-[13px]">{selectPlaceholder}</span>
              )}
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput
              placeholder={t('multiSelect.searchOrCreate', 'Search or create...')}
              value={query}
              onValueChange={setQuery}
              autoFocus
            />
            <CommandList>
              <CommandEmpty>
                {field.allowCustomOptions
                  ? query.trim().length > 0
                    ? null
                    : t('multiSelect.typeToCreate', 'Type to create...')
                  : t('multiSelect.noResultsFound', 'No results found')}
              </CommandEmpty>
              <CommandGroup>
                {options
                  .filter(opt =>
                    query.trim().length === 0
                      ? true
                      : opt.label.toLowerCase().includes(query.toLowerCase()) ||
                        opt.value.toLowerCase().includes(query.toLowerCase())
                  )
                  .map(option => {
                    const isReadOnlyOption = readOnlySet.has(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={() => handleToggle(option.value)}
                        className={cn('flex items-center gap-2', isReadOnlyOption && 'cursor-not-allowed opacity-70')}
                        aria-disabled={field.readonly || isReadOnlyOption}
                        title={isReadOnlyOption ? readOnlyTooltip : undefined}
                        data-testid={`tid.genogram.option.${field.id}.${option.value}`}
                      >
                        <Checkbox
                          checked={localSelected.includes(option.value)}
                          onCheckedChange={() => handleToggle(option.value)}
                          disabled={field.readonly || isReadOnlyOption}
                        />
                        {renderOptionVisual(option, translate(option.label) || option.label)}
                      </CommandItem>
                    );
                  })}
              </CommandGroup>
              {field.allowCustomOptions && query.trim().length > 0 && !options.some(o => o.label.toLowerCase() === query.toLowerCase() || o.value.toLowerCase() === query.toLowerCase()) && !localSelected.includes(query) && (
                <CommandGroup forceMount>
                  <CommandItem
                    forceMount
                    value={`__create__${query}`}
                    onSelect={() => {
                      handleAddCustom(query);
                      setQuery('');
                    }}
                  >
                    Create &ldquo;{query}&rdquo;
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
    </div>
  );
};
